import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

export default function TeamDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/tickets/dashboard/summary');
        setSummary(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load team dashboard');
        if (e.response?.status === 403) navigate('/tickets');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout title="Team Workload">
      <div className="max-w-6xl mx-auto space-y-6" data-testid="team-dashboard-page">
        <Button
          variant="outline"
          onClick={() => navigate('/tickets')}
          className="w-fit"
          data-testid="team-back-btn"
        >
          <ArrowLeft size={14} className="mr-1" /> Back to tickets
        </Button>

        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : !summary ? null : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Total tickets" value={summary.total_tickets} />
              <StatCard label="Open" value={summary.by_status?.open || 0} />
              <StatCard label="In progress" value={summary.by_status?.in_progress || 0} />
              <StatCard
                label="Overdue"
                value={summary.overdue_count}
                highlight={summary.overdue_count > 0}
              />
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="p-4 border-b font-semibold">Per-user workload</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
                      <tr>
                        <th className="px-4 py-3">User</th>
                        <th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Open</th>
                        <th className="px-4 py-3">In progress</th>
                        <th className="px-4 py-3">Blocked</th>
                        <th className="px-4 py-3">Completed</th>
                        <th className="px-4 py-3">Overdue</th>
                        <th className="px-4 py-3">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {summary.per_user?.length ? summary.per_user.map((row) => (
                        <tr key={row.user_id} data-testid={`workload-row-${row.user_id}`}>
                          <td className="px-4 py-3 font-medium">{row.name}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{row.role}</td>
                          <td className="px-4 py-3">{row.open}</td>
                          <td className="px-4 py-3">{row.in_progress}</td>
                          <td className="px-4 py-3">{row.blocked}</td>
                          <td className="px-4 py-3">{row.completed}</td>
                          <td className="px-4 py-3">
                            {row.overdue > 0 ? (
                              <Badge className="bg-red-100 text-red-700 border-0">
                                <AlertTriangle size={10} className="mr-1" /> {row.overdue}
                              </Badge>
                            ) : (
                              <span className="text-gray-500">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-semibold">{row.total}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                            Nobody has any tickets yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div
      className={
        'rounded-xl border px-4 py-3 ' +
        (highlight ? 'bg-red-50 border-red-200' : 'bg-white')
      }
    >
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div
        className={
          'text-2xl font-semibold ' + (highlight ? 'text-red-700' : 'text-gray-900')
        }
      >
        {value}
      </div>
    </div>
  );
}
